import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { fireLastTouch } from "@/lib/attribution";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async ({ silent = false } = {}) => {
        // Skip network call entirely for logged-out visitors — no token means
        // there is no session to fetch, and a 401 here spams the browser console
        // on every public page load.
        if (silent && typeof window !== "undefined" && !localStorage.getItem("simonara_user_token")) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth({ silent: true });
    }, [checkAuth]);

    const establishSession = (data) => {
        if (data.session_token) localStorage.setItem("simonara_user_token", data.session_token);
        const { session_token, ...u } = data;
        setUser(u);
        // If the user landed on a UTM-tagged URL (e.g. `/?utm_source=twitter`)
        // BEFORE logging in, `captureFirstTouchAttribution` on App boot
        // couldn't fire /attribution/touch (no token yet). Fire it now that we
        // have one — safe no-op if the URL has no UTMs.
        fireLastTouch();
        return u;
    };

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        return establishSession(data);
    };

    const register = async (payload) => {
        // Attach first-touch attribution (utm_* + landing_page + referrer)
        // stashed in localStorage on first visit. Never overwritten by later
        // visits — matches the backend "first-touch wins" policy.
        try {
            const raw = typeof window !== "undefined" && window.localStorage.getItem("simonara_attribution");
            if (raw) {
                const attr = JSON.parse(raw);
                payload = { ...payload, ...attr };
            }
        } catch { /* attribution is best-effort */ }
        const { data } = await api.post("/auth/register", payload);
        return establishSession(data);
    };

    const googleSession = async (sessionId, role) => {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId, role });
        return establishSession(data);
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        localStorage.removeItem("simonara_user_token");
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, googleSession, logout, checkAuth, refresh: checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
};

// After auth: claim any pending job intake, then return the right dashboard path.
export const resolvePostAuthPath = async (user) => {
    if (user.role === "employer") {
        const pending = localStorage.getItem("simonara_pending_intake");
        if (pending) {
            try {
                const { intake_id, claim_token } = JSON.parse(pending);
                await api.post("/employer/intakes/claim", { intake_id, claim_token });
            } catch { /* intake may be gone; continue */ }
            localStorage.removeItem("simonara_pending_intake");
        }
        return "/employer/dashboard";
    }
    if (user.role === "hiring_team") return "/team/dashboard";
    if (user.role === "recruiter") return "/recruiter/dashboard";
    return "/professional/dashboard";
};
